import { TextInputV2 } from '@components/atoms';
import {
	Box,
	Group,
	Radio as NativeRadio,
	Space,
	Skeleton,
} from '@mantine/core';
import classes from './ProfileForm.module.css';
import { useMediaQuery } from '@mantine/hooks';
import {
	isNotEmpty,
	matches,
	useForm,
} from '@mantine/form';
import { useTranslation } from 'react-i18next';
import {
	REGEX_EMAIL,
	REGEX_PHONE_NUMBER,
} from '@config/constants/regex';
import { Button } from '@components/atoms/Button';
import { ProfilePictureUpload } from '@components/molecules/ProfilePictureUpload';
import useStore from '@store/index';
import { useEffect, useState } from 'react';
import { useUpdateProfile } from '@features/account-settings/api/update-profile';
import { useWhoAmI } from '@api/get-whoami';
import { useUploadUserPrivatePicture } from '@api/upload-user-private-picture';

export const ProfileForm = () => {
	/* ******************************** HOOKS ******************************* */
	const [pictureData, setPictureData] =
		useState<null | FormData>(null);

	const { t } = useTranslation();

	// Validators
	const formContact = useForm<any>({
		initialValues: {
			firstName: '',
			lastName: '',
			email: '',
			language: 'fr',
			phoneNumber: '',
			avatar: null,
		},
		validate: {
			firstName: isNotEmpty(
				t('global.firstNameRequired')
			),
			lastName: isNotEmpty(t('global.lastNameRequired')),
			email: matches(
				REGEX_EMAIL,
				'form.error.invalidEmail'
			),
			phoneNumber: matches(
				REGEX_PHONE_NUMBER,
				'form.error.invalidPhoneNumber'
			),
		},
	});

	const { getUser } = useStore();

	const user = getUser();

	const uploadPictureMutation =
		useUploadUserPrivatePicture({
			mutationConfig: {
				onSuccess(data, variables, context) {
					formContact.setFieldValue('avatar', data.id);
					formContact.validate();
					if (formContact.isValid()) {
						handleUpdateProfile(formContact.getValues());
					}
				},
			},
		});

	const updateProfileMutation = useUpdateProfile({
		mutationConfig: {
			onSuccess(data, variables, context) {
				formContact.resetDirty();
			},
		},
	});

	const {
		data: currentUser,
		isFetched: isFetchedCurrentUser,
		isPending: isPendingCurrentUser,
	} = useWhoAmI();

	useEffect(() => {
		if (isFetchedCurrentUser && currentUser) {
			formContact.setValues({
				firstName: currentUser.firstName,
				lastName: currentUser.lastName,
				email: currentUser.email,
				language: currentUser.locale.language,
				phoneNumber:
					currentUser?.phoneNumber == null
						? ''
						: currentUser?.phoneNumber,
				avatar: currentUser.avatar || null,
			});
			formContact.resetDirty();
		}
	}, [isFetchedCurrentUser]);

	const handleUpdateProfile = (data: any) => {
		const {
			language,
			phoneNumber,
			firstName,
			lastName,
			avatar,
		} = data;
		updateProfileMutation.mutate({
			id: user.id,
			...(formContact.isDirty('language')
				? {
						locale: {
							language,
						},
					}
				: {}),
			...(formContact.isDirty('avatar')
				? {
						avatar,
					}
				: {}),
			...(formContact.isDirty('firstName')
				? {
						firstName,
					}
				: {}),
			...(formContact.isDirty('lastName')
				? {
						lastName,
					}
				: {}),
			...(formContact.isDirty('phoneNumber')
				? {
						phoneNumber,
					}
				: {}),
			...(formContact.isDirty('phoneNumber')
				? {
						phoneNumber:
							phoneNumber !== '' ? phoneNumber : null,
					}
				: {}),
		});
	};

	const handleSaveChanges = () => {
		if (formContact.getValues().avatar !== null) {
			uploadPictureMutation.mutate(pictureData);
		} else {
			formContact.validate();
			if (formContact.isValid()) {
				handleUpdateProfile(formContact.getValues());
			}
		}
	};

	const handleOnUploadProfile = (
		formData: FormData | string | null
	) => {
		if (typeof formData !== 'string') {
			formContact.setFieldValue('avatar', formData);
			setPictureData(formData);
		} else {
			formContact.setFieldValue('avatar', null);
		}
	};

	/* ******************************** Media Query ******************************* */
	const isBigScreen = useMediaQuery('(min-width: 120em)');

	if (isPendingCurrentUser) {
		return (
			<Box
				style={{
					display: 'flex',
					flex: 1.5,
					flexDirection: 'column',
					gap: '20px',
				}}
			>
				<Group justify="space-between">
					<Skeleton
						height={100}
						width={100}
						radius="md"
						circle
					/>
					<Skeleton height={40} width={260} radius="md" />
				</Group>
				<Skeleton
					height={40}
					width={'100%'}
					radius="md"
				/>
				<Skeleton
					height={40}
					width={'100%'}
					radius="md"
				/>
				<Skeleton
					height={40}
					width={'100%'}
					radius="md"
				/>
				<Skeleton
					height={40}
					width={'100%'}
					radius="md"
				/>
				<Skeleton
					height={40}
					width={'100%'}
					radius="md"
				/>
				<Space h={30} />
				<Skeleton
					height={40}
					width={'100%'}
					radius="md"
				/>
				<Skeleton
					height={40}
					width={'100%'}
					radius="md"
				/>
				<Skeleton
					height={40}
					width={'100%'}
					radius="md"
				/>
			</Box>
		);
	}

	return (
		<Box
			style={{
				display: 'flex',
				flex: 1.5,
			}}
		>
			{/**************************** Delete confirmation modal ****************************/}

			<form className={classes.form}>
				<ProfilePictureUpload
					onUpload={handleOnUploadProfile}
					src={currentUser?.avatar || ''}
				/>
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<TextInputV2
							key={formContact.key('firstName')}
							withAsterisk
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.firstName')}
							placeholder={t('global.firstName')}
							{...formContact.getInputProps('firstName')}
						/>
					</Box>
					<Box className={classes.containerInput}>
						<TextInputV2
							key={formContact.key('lastName')}
							withAsterisk
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.lastName')}
							placeholder={t('global.lastName')}
							{...formContact.getInputProps('lastName')}
						/>
					</Box>
				</Box>
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<TextInputV2
							key={formContact.key('email')}
							disabled
							withAsterisk
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.email')}
							placeholder={t('global.email')}
							{...formContact.getInputProps('email')}
						/>
					</Box>
				</Box>
				<Box className={classes.row}>
					<Box
						style={{
							width: '30%',
						}}
					>
						<NativeRadio.Group
							withAsterisk
							label={t('global.language')}
							key={formContact.key('language')}
							{...formContact.getInputProps('language')}
						>
							<Group gap={'37px'} pt={'md'}>
								<NativeRadio
									variant="outline"
									size={isBigScreen ? 'sm' : 'xs'}
									label={'Fr'}
									value={'fr'}
								/>
								<NativeRadio
									variant="outline"
									size={isBigScreen ? 'sm' : 'xs'}
									label={'En'}
									value={'en'}
								/>
							</Group>
						</NativeRadio.Group>
					</Box>
					<Box className={classes.containerInput}>
						<TextInputV2
							autoComplete={'off'}
							key={formContact.key('phoneNumber')}
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.phoneNumber')}
							placeholder={t('global.phoneNumber')}
							{...formContact.getInputProps(
								'phoneNumber'
							)}
						/>
					</Box>
				</Box>
				{/* Row for Buttons */}
				<Box className={classes.rowButtons}>
					<Box className={classes.containerButton}>
						<Button
							disabled={!formContact.isDirty()}
							loading={updateProfileMutation.isPending}
							fullWidth
							size={'md'}
							onClick={handleSaveChanges}
						>
							{t('global.saveChanges')}
						</Button>
					</Box>
				</Box>
			</form>
		</Box>
	);
};
